// @ts-nocheck

import { merge } from 'lodash';
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';

import { Valid } from '../../../assets/svg/common';

type RawSwitchThemeType = {
  mainBackgroundColor: string;
  borderBackgroundColor: string;
  circleBackgroundColor: string;
  circleColor: string;
};

export type SwitchThemeType = {
  toggled?: RawSwitchThemeType;
  untoggled?: RawSwitchThemeType;
  labelColor?: string;
  labelBackgroundColor?: string;
};

const defaultTheme: SwitchThemeType = {
  toggled: {
    mainBackgroundColor: '#46cc8a',
    borderBackgroundColor: '#ffffff',
    circleBackgroundColor: '#ffffff',
    circleColor: '#1a1c24',
  },
  untoggled: {
    mainBackgroundColor: '#43485a',
    borderBackgroundColor: '#a9aeb1',
    circleBackgroundColor: '#c1c9cf',
    circleColor: '#1a1c24',
  },
  labelColor: '#c1c9cf',
  labelBackgroundColor: 'transparent',
};

const SWITCH_WIDTH = 2.3;
const SWITCH_HEIGHT = 1.2;
const SWITCH_CIRCLE_DIAMETER = SWITCH_HEIGHT * 1.3;
const SWITCH_CIRCLE_CORRECTION = 0.25;
const ANIMATION_DURATION = 300;

const Wrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  transition: filter ${ANIMATION_DURATION}ms ease-in-out;

  ${({ disabled, $isPassive }) => css`
    cursor: pointer;
    ${disabled || $isPassive
      ? css`
          pointer-events: none;
        `
      : undefined}
    ${disabled
      ? css`
          filter: brightness(0.6);
        `
      : undefined}
  `}
`;

const SwitchWrapper = styled.div`
  position: relative;
  width: ${SWITCH_WIDTH}em;
  height: ${SWITCH_HEIGHT}em;

  margin-right: ${SWITCH_CIRCLE_DIAMETER / 2 - SWITCH_CIRCLE_CORRECTION}em;
`;

const SwitchBackground = styled.div`
  width: 100%;
  height: 100%;

  background: white;
  border-radius: 1em;

  transition:
    background-color var(--Switch-colorTransitionDuration, ${ANIMATION_DURATION}ms) ease-in-out,
    border var(--Switch-colorTransitionDuration, ${ANIMATION_DURATION}ms) ease-in-out,
    filter ${ANIMATION_DURATION}ms ease-in-out;

  ${({ $theme, $toggled, $isPassive, $disabled }) => {
    if ($theme) {
      if ($toggled) {
        return css`
          background-color: ${$theme.toggled.mainBackgroundColor};
          border: 1px solid ${$theme.toggled.borderBackgroundColor};
          ${$isPassive && !$disabled
            ? css`
                filter: brightness(0.6);
              `
            : undefined}
        `;
      }
      return css`
        background-color: ${$theme.untoggled.mainBackgroundColor};
        border: 1px solid ${$theme.untoggled.borderBackgroundColor};
      `;
    }
  }}
`;

const SwitchCircle = styled.div`
  position: absolute;
  top: 50%;
  left: -${SWITCH_CIRCLE_CORRECTION}em;

  width: ${SWITCH_CIRCLE_DIAMETER}em;
  height: ${SWITCH_CIRCLE_DIAMETER}em;

  transition:
    transform ${ANIMATION_DURATION}ms ease-in-out,
    background-color var(--Switch-colorTransitionDuration, ${ANIMATION_DURATION}ms) ease-in-out,
    color var(--Switch-colorTransitionDuration, ${ANIMATION_DURATION}ms) ease-in-out;

  border-radius: 50%;

  ${({ $theme, $toggled }) => {
    if ($theme) {
      if ($toggled) {
        return css`
          background-color: ${$theme.toggled.circleBackgroundColor};
          border: 1px solid ${$theme.toggled.circleColor};
          color: ${$theme.toggled.circleColor};
          transform: translateY(-50%)
            translateX(${SWITCH_WIDTH - SWITCH_CIRCLE_DIAMETER / 2 - SWITCH_CIRCLE_CORRECTION}em);
        `;
      }
      return css`
        background-color: ${$theme.untoggled.circleBackgroundColor};
        border: 1px solid ${$theme.untoggled.circleColor};
        color: ${$theme.untoggled.circleColor};
        transform: translateY(-50%) translateX(0em);
      `;
    }
  }};
`;

const StyledValid = styled(Valid)`
  & .valid-svg-border {
    fill: none;
  }
`;

const LabelWrapper = styled.div`
  ${({ $theme }) => {
    if ($theme) {
      return css`
        background-color: ${$theme.labelBackgroundColor};
      `;
    }
  }}
`;

const Label = styled.div`
  ${({ $theme }) => {
    if ($theme) {
      return css`
        color: ${$theme.labelColor};
      `;
    }
  }}
`;

type SwitchProps = {
  className?: string;
  disabled?: boolean;
  toggled?: boolean;
  label: string;
  onToggle?: (toggled: boolean) => void;
  theme?: SwitchThemeType;
  passive?: boolean;
  PrimaryIcon?: unknown;
  SecondaryIcon?: unknown;
  autoToggleOnRender?: boolean;
  'data-testid'?: string;
};

const Switch = ({
  className,
  disabled = false,
  toggled = false,
  label = '',
  onToggle = () => {},
  theme = defaultTheme,
  passive = false,
  PrimaryIcon,
  SecondaryIcon,
  autoToggleOnRender = false,
  'data-testid': testId,
}: SwitchProps) => {
  const smartTheme = useMemo(() => {
    if (theme) {
      const dest = {};
      merge(dest, defaultTheme, theme);
      return dest;
    }
    return defaultTheme;
  }, [theme]);

  const InnerCircleIcon = useMemo(() => (toggled ? () => <StyledValid /> : () => null), [toggled]);

  const onToggleClick = useCallback(() => {
    if (!disabled) {
      onToggle(!toggled);
    }
  }, [disabled, onToggle, toggled]);

  const autoToggledOnRender = useRef(false);
  useLayoutEffect(() => {
    if (autoToggleOnRender && !autoToggledOnRender.current) {
      onToggle(true);
      autoToggledOnRender.current = true;
    }
  }, [autoToggleOnRender, onToggle]);

  return (
    <Wrapper
      className={className}
      onClick={onToggleClick}
      disabled={disabled}
      $isPassive={passive}
      data-testid={`${testId ?? 'switch'}`}
    >
      <SwitchWrapper className="switch-sub-wrapper" data-testid={toggled ? 'toggled' : 'untoggled'}>
        <SwitchBackground $theme={smartTheme} $toggled={toggled} $isPassive={passive} $disabled={disabled} />
        <SwitchCircle $theme={smartTheme} $toggled={toggled}>
          <InnerCircleIcon />
        </SwitchCircle>
      </SwitchWrapper>
      {PrimaryIcon ? <PrimaryIcon /> : null}
      {SecondaryIcon ? <SecondaryIcon /> : null}
      {label ? (
        <LabelWrapper className="switch-label-wrapper" $theme={smartTheme}>
          <Label $theme={smartTheme} className="switch-label">
            {label}
          </Label>
        </LabelWrapper>
      ) : null}
    </Wrapper>
  );
};

export default Switch;
